import type { MessageListType, ThreadId } from '../types';
import { MAIN_THREAD_ID } from '../api/types';

import { IS_MOCKED_CLIENT } from '../config';

let parsedInitialLocationHash: Record<string, string> | undefined;
let messageHash: string | undefined;
let isAlreadyParsed = false;
let initialLocationHash = window.location.hash;

// A link opened COLD in a browser arrives as a PATH, not a hash: s.jaryan.app/+HASH.
// Upstream never had to handle that, because on t.me the links live on a different
// host from the web app — a t.me link is always a navigation away, never a cold start
// of the app itself. Here the link host and the app host are the same origin, so the
// path is still in the URL when the app boots and nothing looks at it.
//
// Captured at module load, before anything can rewrite the URL.
const initialLocationPath = window.location.pathname;
const initialLocationSearch = window.location.search;

// Only the shapes this deployment's links take. Any other path would otherwise be handed
// to openTelegramLink and read as a username, which is worse than ignoring it. A username
// is held to Telegram's own shape — a letter, then 3 to 31 letters, digits or
// underscores — so a stray path such as /a or /get is never looked up as one.
//
// Paths of more than one segment boot only because public/jaryan-base.js puts a <base>
// at the root; without it the bundle resolves under the path and the page stays blank.
const RE_LINK_PATHS = [
  /^\/(\+[^/]+)\/?$/, // invite
  /^\/([a-zA-Z]\w{3,31}(?:\/\d+){0,2})\/?$/, // username, then a message, or a topic and a message
  /^\/(c\/\d+(?:\/\d+){1,2})\/?$/, // a message in a chat without a username
  /^\/((?:addstickers|addemoji)\/\w+)\/?$/, // a sticker or emoji set
];

let isLinkPathConsumed = false;

// Consumed rather than read: isSynced goes false and true again on every reconnect,
// and the path lives in a module const that outlives any change to window.location.
// Without this the link reopens on each reconnect for the rest of the page session.
//
// The query is kept, which is where a bot's ?start= parameter travels.
export function consumeInitialLinkPath(): string | undefined {
  if (isLinkPathConsumed) return undefined;
  const match = RE_LINK_PATHS.map((re) => initialLocationPath.match(re)).find(Boolean);
  if (!match) return undefined;
  isLinkPathConsumed = true;
  return `${match[1]}${initialLocationSearch}`;
}

export function resetInitialLocationHash() {
  isAlreadyParsed = false;
  messageHash = undefined;
  parsedInitialLocationHash = undefined;
  initialLocationHash = '';
}

export function resetLocationHash() {
  window.location.hash = '';
}

export const createLocationHash = (chatId: string, type: MessageListType, threadId: ThreadId): string => {
  const displayType = type === 'thread' ? undefined : type;
  const parts = threadId === MAIN_THREAD_ID ? [chatId, displayType] : [chatId, threadId, displayType];

  return parts.filter(Boolean).join('_');
};

export function parseLocationHash(currentUserId?: string) {
  parseInitialLocationHash();

  if (!messageHash) return undefined;

  const parts = messageHash.split('_');
  let chatId: string | undefined;
  let type: string | undefined;
  let threadId: string | undefined;
  if (parts.length === 1) {
    chatId = parts[0];
  } else if (parts.length === 2) {
    const isType = ['thread', 'pinned', 'scheduled'].includes(parts[1]);
    chatId = parts[0];
    type = isType ? parts[1] : 'thread';
    threadId = !isType ? parts[1] : undefined;
  } else if (parts.length >= 3) {
    [chatId, threadId, type] = parts;
  }
  if (!chatId?.match(/^-?\d+$/)) return undefined;

  const isType = ['thread', 'pinned', 'scheduled'].includes(type!);

  const castedThreadId = (chatId === currentUserId ? threadId : Number(threadId)) || MAIN_THREAD_ID;

  return {
    chatId,
    type: type && isType ? (type as MessageListType) : 'thread',
    threadId: castedThreadId,
  };
}

export const createMessageHashUrl = (chatId: string, type: MessageListType, threadId: ThreadId): string => {
  const url = new URL(window.location.href);
  url.hash = createLocationHash(chatId, type, threadId);
  return url.href;
};

export function parseInitialLocationHash() {
  if (parsedInitialLocationHash) return parsedInitialLocationHash;

  if (isAlreadyParsed) return undefined;

  const locationHash = getInitialLocationHash();
  if (!locationHash) return undefined;

  let parsedHash = locationHash.replace(/^#/, '');
  if (parsedHash.includes('?')) {
    [messageHash, parsedHash] = parsedHash.split('?');
    if (!IS_MOCKED_CLIENT) {
      window.location.hash = messageHash;
    }
  } else if (parsedHash.includes('=')) {
    if (!IS_MOCKED_CLIENT) {
      window.location.hash = '';
    }
  }

  parsedInitialLocationHash = parsedHash.includes('=') ? parsedHash.split('&').reduce((acc, cur) => {
    const [key, value] = cur.split('=');
    acc[key] = value;
    return acc;
  }, {} as Record<string, string>) : undefined;
  isAlreadyParsed = true;
  if (!parsedInitialLocationHash) {
    messageHash = parsedHash;
  }

  return parsedInitialLocationHash;
}

export function clearWebTokenAuth() {
  if (!parsedInitialLocationHash) return;

  delete parsedInitialLocationHash.tgWebAuthToken;
}

export function getInitialLocationHash() {
  return initialLocationHash;
}
